import { ExternalReturnHubType } from "@redotech/account-common/address";
import type { TeamLocation } from "@redotech/admin-sdk/rpc/schema/locations/get-team-locations";
import { ProcessingFeeBasis } from "@redotech/crdb-enums/fulfillment/processing-fee-basis";
import { useRequiredContext } from "@redotech/react-util/context";
import { useRpcData } from "@redotech/react-util/rpc-data";
import Edit01Icon from "@redotech/redo-icons/edit-01.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import type { AddressCollection } from "@redotech/redo-return-core-common/address-collection";
import {
  returnsCenterIdFormat,
  type ReturnsCenterId,
} from "@redotech/redo-warehouse-id";
import { toast } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonTabGroup } from "@redotech/redo-web/arbiter-components/checkbox/redo-button-tab-group";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { useRpcMutation } from "@redotech/redo-web/rpc-query/use-rpc-mutation";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import type { InputSchema } from "@redotech/ui/form";
import { memo, useCallback, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../../app/redo-admin-rpc-client-provider";
import {
  processingFeeErrors,
  returnsCenterFeeBasisErrors,
  returnsCenterScheduleDaysErrors,
  type availableReturnsCentersInput,
  type ExternalLocationHubFormConfig,
  type ReturnsCenterConfigFormValue,
} from "./redo-managed-returns-settings-form";

type AvailableReturnsCentersForm = InputSchema.Input<
  typeof availableReturnsCentersInput
>;

interface ReturnsCenterRowData {
  id: string;
  label: string;
  schedule: string;
  addressSummary: string;
  provider: string;
  locationId: string;
  ownerTeamName: string;
  isPartnerReturnsCenter: boolean;
  refulfillment: boolean;
}

const HUB_DISPLAY_NAMES: Record<ExternalReturnHubType, string> = {
  [ExternalReturnHubType.RETURNBEAR]: "ReturnBear",
  [ExternalReturnHubType.BLUEYONDER]: "Blue Yonder",
  [ExternalReturnHubType.SHIPMONK]: "ShipMonk",
  [ExternalReturnHubType.SHIPHERO]: "ShipHero",
  [ExternalReturnHubType.RYDER]: "Ryder",
  [ExternalReturnHubType.DEPOSCO]: "Deposco",
  [ExternalReturnHubType.NETSUITE]: "NetSuite",
  [ExternalReturnHubType.MINTSOFT]: "Mintsoft",
};

function summarizeAddress(config: ReturnsCenterConfigFormValue): string {
  const parts = [config.shipToCity, config.shipToState, config.shipToCountry]
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "";
}

function formatSchedule(days: string): string {
  const trimmed = days.trim();
  if (!trimmed) {
    return "Not scheduled";
  }
  const n = parseInt(trimmed, 10);
  if (isNaN(n)) {
    return trimmed;
  }
  // A stored 0 means consolidation is off, same as no schedule at all.
  if (n === 0) {
    return "Not scheduled";
  }
  if (n === 1) {
    return "Every day";
  }
  return `Every ${n} days`;
}

function buildDetailLine(row: ReturnsCenterRowData): string {
  const parts: string[] = [];
  if (row.ownerTeamName) {
    parts.push(row.ownerTeamName);
  }
  if (row.schedule !== "Not scheduled") {
    parts.push(row.schedule);
  }
  if (row.addressSummary) {
    parts.push(row.addressSummary);
  }
  const providerName = row.provider
    ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      (HUB_DISPLAY_NAMES[row.provider as ExternalReturnHubType] ?? row.provider)
    : "";
  if (providerName) {
    parts.push(providerName);
  }
  return parts.join(" · ");
}

const EMPTY_CONFIG: Omit<ReturnsCenterConfigFormValue, "returnsCenterId"> = {
  consolidationScheduleDays: "",
  shipToName: "",
  shipToStreet1: "",
  shipToStreet2: "",
  shipToCity: "",
  shipToState: "",
  shipToZip: "",
  shipToCountry: "",
  refulfillment: false,
  processingFee: "",
  processingFeeBasis: null,
};

function centerLabel(center: {
  name: string;
  city: string | null;
  state: string | null;
}): string {
  return `${center.name}${center.city ? ` - ${center.city}` : ""}${center.state ? `, ${center.state}` : ""}`;
}

const ROW_BORDER: React.CSSProperties = {
  borderBottom: "1px solid var(--redo-colors-border-border-secondary)",
};

const ROW_MIN_HEIGHT: React.CSSProperties = { minHeight: 56 };

const ReturnsCenterRow = memo(function ReturnsCenterRow({
  row,
  onEdit,
  onRemove,
  onToggleRefulfillment,
  showRefulfillmentToggle,
  showEditButton,
  disabled,
  isLast,
}: {
  row: ReturnsCenterRowData;
  onEdit?: (centerId: string) => void;
  onRemove?: (centerId: string) => void;
  onToggleRefulfillment?: (centerId: string, value: boolean) => void;
  showRefulfillmentToggle?: boolean;
  showEditButton?: boolean;
  disabled: boolean;
  isLast: boolean;
}) {
  const detailLine = buildDetailLine(row);

  const handleToggle = useCallback(
    (value: boolean) => {
      onToggleRefulfillment?.(row.id, value);
    },
    [onToggleRefulfillment, row.id],
  );

  return (
    <Flex
      align="center"
      cursor={onEdit ? "pointer" : undefined}
      dir="row"
      gap="lg"
      hoverColor={onEdit ? "secondary" : undefined}
      justify="space-between"
      onClick={onEdit ? () => onEdit(row.id) : undefined}
      p="lg"
      style={isLast ? ROW_MIN_HEIGHT : { ...ROW_BORDER, ...ROW_MIN_HEIGHT }}
    >
      <Flex dir="column" gap="xxs" grow="1">
        <Flex align="center" dir="row" gap="xs" wrap="wrap">
          <Text fontSize="xs" fontWeight="medium">
            {row.label}
          </Text>
          {row.isPartnerReturnsCenter && (
            <RedoBadge color="purple" size="sm" text="Partner return center" />
          )}
        </Flex>
        {detailLine && (
          <Text fontSize="xxs" textColor="secondary">
            {detailLine}
          </Text>
        )}
      </Flex>
      {showRefulfillmentToggle && (
        <Flex onClick={(e: React.MouseEvent) => e.stopPropagation()} shrink={0}>
          <RedoToggle
            disabled={disabled}
            setValue={handleToggle}
            size="sm"
            value={row.refulfillment}
          />
        </Flex>
      )}
      {showEditButton && onEdit && (
        <Flex
          dir="row"
          gap="xs"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          shrink={0}
        >
          <RedoButton
            disabled={disabled}
            hierarchy="tertiary"
            IconLeading={Edit01Icon}
            onClick={() => onEdit(row.id)}
            size="xs"
            text="Edit"
          />
        </Flex>
      )}
      {onRemove && (
        <Flex
          dir="row"
          gap="xs"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          shrink={0}
        >
          <RedoButton
            aria-label="Remove"
            disabled={disabled}
            hierarchy="tertiary"
            IconLeading={TrashIcon}
            onClick={() => onRemove(row.id)}
            size="xs"
          />
        </Flex>
      )}
    </Flex>
  );
});

export const AvailableReturnsCentersTable = memo(
  function AvailableReturnsCentersTable({
    input,
    disabled,
    hubMappings,
    onHubMappingChange,
    headerRight,
    showRefulfillmentToggle,
    showEditButton,
    editMode,
    readOnly,
    teamId,
    globalFeeBasis,
  }: {
    input: AvailableReturnsCentersForm;
    disabled: boolean;
    hubMappings?: Record<string, ExternalLocationHubFormConfig>;
    onHubMappingChange?: (
      centerId: string,
      hub: ExternalLocationHubFormConfig,
    ) => void;
    headerRight?: React.ReactNode;
    showRefulfillmentToggle?: boolean;
    /**
     * Show an explicit Edit button on each row and make rows clickable even
     * when `readOnly` (which only suppresses add/remove).
     */
    showEditButton?: boolean;
    editMode?: "full" | "hub-only";
    readOnly?: boolean;
    globalFeeBasis?: ProcessingFeeBasis | null;
    /**
     * Team being configured. Required for editing a center's consolidation
     * ship-to address and linked merchant locations.
     */
    teamId: string;
  }) {
    const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

    const { value: returnsCentersData, pending: returnsCentersPending } =
      useRpcData(rpcClient, "getReturnsCenters", { input: { sort: null } });

    const allReturnsCenters = useMemo(
      () => returnsCentersData?.returnsCenters ?? [],
      [returnsCentersData],
    );

    const configs = input.value;

    const [editingCenterId, setEditingCenterId] = useState<string | null>(null);
    const [editDraft, setEditDraft] =
      useState<ReturnsCenterConfigFormValue | null>(null);
    const [editHubDraft, setEditHubDraft] =
      useState<ExternalLocationHubFormConfig | null>(null);

    const openEditModal = useCallback(
      (centerId: string) => {
        const existing = configs.find((c) => c.returnsCenterId === centerId);
        setEditDraft(
          existing ?? { returnsCenterId: centerId, ...EMPTY_CONFIG },
        );
        setEditHubDraft(
          hubMappings?.[centerId] ?? { provider: "", locationId: "" },
        );
        setEditingCenterId(centerId);
      },
      [configs, hubMappings],
    );

    const closeEditModal = useCallback(() => {
      setEditingCenterId(null);
      setEditDraft(null);
      setEditHubDraft(null);
    }, []);

    const saveEditModal = useCallback(() => {
      if (!editDraft) {
        return;
      }
      input.setValue(
        configs.map((c) =>
          c.returnsCenterId === editDraft.returnsCenterId ? editDraft : c,
        ),
      );
      if (hubMappings && editHubDraft && onHubMappingChange) {
        onHubMappingChange(editDraft.returnsCenterId, editHubDraft);
      }
      closeEditModal();
    }, [
      editDraft,
      editHubDraft,
      configs,
      input,
      hubMappings,
      onHubMappingChange,
      closeEditModal,
    ]);

    const removeCenter = useCallback(
      (centerId: string) => {
        input.setValue(configs.filter((c) => c.returnsCenterId !== centerId));
      },
      [configs, input],
    );

    const addCenter = useCallback(
      (centerId: string) => {
        input.setValue([
          ...configs,
          { returnsCenterId: centerId, ...EMPTY_CONFIG },
        ]);
      },
      [configs, input],
    );

    const selectedCenterIds = useMemo(
      () => new Set(configs.map((c) => c.returnsCenterId)),
      [configs],
    );

    const toggleRefulfillment = useCallback(
      (centerId: string, value: boolean) => {
        input.setValue(
          configs.map((c) =>
            c.returnsCenterId === centerId ? { ...c, refulfillment: value } : c,
          ),
        );
      },
      [configs, input],
    );

    const rowsOfData: ReturnsCenterRowData[] = useMemo(() => {
      return configs.map((config) => {
        const center = allReturnsCenters.find(
          (c) => c.id === config.returnsCenterId,
        );
        const mapping = hubMappings?.[config.returnsCenterId];
        return {
          id: config.returnsCenterId,
          label: center ? centerLabel(center) : config.returnsCenterId,
          schedule: formatSchedule(config.consolidationScheduleDays),
          addressSummary: summarizeAddress(config),
          provider: mapping?.provider ?? "",
          locationId: mapping?.locationId ?? "",
          ownerTeamName: center?.ownerTeamName ?? "",
          isPartnerReturnsCenter: center ? !center.hasLinkedLocation : false,
          refulfillment: config.refulfillment,
        };
      });
    }, [allReturnsCenters, configs, hubMappings]);

    const addOptions: RedoListItem<string>[] = useMemo(() => {
      return allReturnsCenters
        .filter((c) => !selectedCenterIds.has(c.id))
        .map((center) => ({
          type: "text" as const,
          id: center.id,
          value: center.id,
          text: centerLabel(center),
        }));
    }, [allReturnsCenters, selectedCenterIds]);

    const handleAddSelected = useCallback(
      ({ item }: { item: RedoListItem<string> }) => {
        addCenter(item.value);
      },
      [addCenter],
    );

    if (returnsCentersPending) {
      return <LoadingRedoAnimation />;
    }

    const editingCenter = editingCenterId
      ? allReturnsCenters.find((c) => c.id === editingCenterId)
      : null;

    const hasAvailableCenters = addOptions.length > 0;

    return (
      <Flex dir="column" gap="none">
        {!readOnly && hasAvailableCenters && (
          <Flex p="lg">
            <RedoSingleSelectDropdownInput
              disabled={disabled}
              options={addOptions}
              optionSelected={handleAddSelected}
              placeholder="Add a returns center..."
              size="xs"
            />
          </Flex>
        )}
        {rowsOfData.length > 0 && (
          <Flex
            align="center"
            justify="space-between"
            pb="sm"
            pt="lg"
            px="lg"
            style={ROW_BORDER}
            w="full"
          >
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              Returns Center
            </Text>
            {showRefulfillmentToggle && (
              <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                Re-fulfillment
              </Text>
            )}
            {headerRight}
          </Flex>
        )}
        <Flex align="stretch" dir="column" gap="none">
          {rowsOfData.map((row, index) => (
            <ReturnsCenterRow
              disabled={disabled}
              isLast={index === rowsOfData.length - 1}
              key={row.id}
              onEdit={!readOnly || showEditButton ? openEditModal : undefined}
              onRemove={readOnly ? undefined : removeCenter}
              onToggleRefulfillment={
                showRefulfillmentToggle ? toggleRefulfillment : undefined
              }
              row={row}
              showEditButton={showEditButton}
              showRefulfillmentToggle={showRefulfillmentToggle}
            />
          ))}
        </Flex>

        {editDraft && editingCenter && (
          <EditReturnsCenterModal
            centerLabel={centerLabel(editingCenter)}
            disabled={disabled}
            draft={editDraft}
            editMode={editMode ?? "full"}
            globalFeeBasis={globalFeeBasis ?? null}
            hasSourceLocation={editingCenter.hasLinkedLocation}
            hubDraft={editHubDraft}
            onClose={closeEditModal}
            onSave={saveEditModal}
            setDraft={setEditDraft}
            setHubDraft={hubMappings ? setEditHubDraft : undefined}
            teamId={teamId}
          />
        )}
      </Flex>
    );
  },
);

interface SelectableShipToAddress {
  name: string;
  street1: string;
  street2?: string;
  city: string;
  state?: string;
  zip: string;
  country: string;
}

/**
 * A consolidation ship-to address is only usable once it has the parts a
 * shipment needs, and a 2-letter ISO country (matching the merchant-side
 * editor and the ConsolidationShipToAddress contract). Anything short of that
 * is not offered as a selectable destination.
 */
function draftToShipToAddress(
  draft: ReturnsCenterConfigFormValue,
): SelectableShipToAddress | null {
  const country = draft.shipToCountry.trim().toUpperCase();
  if (
    !draft.shipToName.trim() ||
    !draft.shipToStreet1.trim() ||
    !draft.shipToCity.trim() ||
    !draft.shipToZip.trim() ||
    country.length !== 2
  ) {
    return null;
  }
  return {
    name: draft.shipToName.trim(),
    street1: draft.shipToStreet1.trim(),
    street2: draft.shipToStreet2.trim() || undefined,
    city: draft.shipToCity.trim(),
    state: draft.shipToState.trim() || undefined,
    zip: draft.shipToZip.trim(),
    country,
  };
}

function addressCollectionToShipToAddress(
  collection: AddressCollection,
): SelectableShipToAddress | null {
  const address = collection.address;
  if (!address) {
    return null;
  }
  const country = address.country.trim().toUpperCase();
  if (
    !address.name.trim() ||
    !address.street1.trim() ||
    !address.city.trim() ||
    !address.zip.trim() ||
    country.length !== 2
  ) {
    return null;
  }
  return {
    name: address.name.trim(),
    street1: address.street1.trim(),
    street2: address.street2?.trim() || undefined,
    city: address.city.trim(),
    state: address.state?.trim() || undefined,
    zip: address.zip.trim(),
    country,
  };
}

function shipToAddressesMatch(
  a: SelectableShipToAddress,
  b: SelectableShipToAddress,
): boolean {
  return (
    a.name === b.name &&
    a.street1 === b.street1 &&
    (a.street2 ?? "") === (b.street2 ?? "") &&
    a.city === b.city &&
    (a.state ?? "") === (b.state ?? "") &&
    a.zip === b.zip &&
    a.country === b.country
  );
}

function formatShipToAddressLine(address: SelectableShipToAddress): string {
  const locality = [
    address.city,
    [address.state, address.zip].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  return [address.street1, address.street2, locality, address.country]
    .filter(Boolean)
    .join(" · ");
}

function makeShipToLocationItem(
  id: string,
  address: SelectableShipToAddress,
  fallbackName = "Saved location",
): RedoListItem<SelectableShipToAddress> {
  return {
    id,
    supportingText: formatShipToAddressLine(address),
    text: address.name || fallbackName,
    type: "text",
    value: address,
  };
}
const EXTERNAL_HUB_OPTIONS: RedoListItem<ExternalReturnHubType | "">[] = [
  { type: "text", id: "", value: "", text: "None" },
  ...// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  (Object.entries(HUB_DISPLAY_NAMES) as [ExternalReturnHubType, string][]).map(
    ([value, text]) => ({ type: "text" as const, id: value, value, text }),
  ),
];

const FEE_BASIS_OPTIONS: { value: ProcessingFeeBasis; el: React.ReactNode }[] =
  [
    { value: ProcessingFeeBasis.PerReturn, el: "Per return" },
    { value: ProcessingFeeBasis.PerItem, el: "Per unit" },
  ];

const EditReturnsCenterModal = memo(function EditReturnsCenterModal({
  centerLabel,
  disabled,
  draft,
  setDraft,
  hubDraft,
  setHubDraft,
  editMode,
  globalFeeBasis,
  hasSourceLocation,
  onClose,
  onSave,
  teamId,
}: {
  centerLabel: string;
  disabled: boolean;
  draft: ReturnsCenterConfigFormValue;
  setDraft: (value: ReturnsCenterConfigFormValue) => void;
  hubDraft: ExternalLocationHubFormConfig | null;
  setHubDraft?: (value: ExternalLocationHubFormConfig) => void;
  editMode: "full" | "hub-only";
  globalFeeBasis: ProcessingFeeBasis | null;
  hasSourceLocation: boolean;
  onClose: () => void;
  onSave: () => void;
  teamId: string;
}) {
  const updateField = useCallback(
    <Field extends keyof ReturnsCenterConfigFormValue>(
      field: Field,
      value: ReturnsCenterConfigFormValue[Field],
    ) => {
      setDraft({ ...draft, [field]: value });
    },
    [draft, setDraft],
  );

  const updateProcessingFee = useCallback(
    (value: string) => {
      const cleared = value.trim() === "";
      setDraft({
        ...draft,
        processingFee: value,
        processingFeeBasis: cleared
          ? null
          : (draft.processingFeeBasis ?? globalFeeBasis),
      });
    },
    [draft, setDraft, globalFeeBasis],
  );

  const addressesQuery = useRpcQuery(
    RedoAdminRpcClientContext,
    "getAddressCollectionsForTeam",
    { teamId: teamId ?? "" },
    { enabled: !!teamId },
  );

  const locationItems = useMemo<RedoListItem<SelectableShipToAddress>[]>(
    () =>
      (addressesQuery.data?.addresses ?? []).flatMap((collection) => {
        const address = addressCollectionToShipToAddress(collection);
        return address
          ? [makeShipToLocationItem(String(collection._id), address)]
          : [];
      }),
    [addressesQuery.data],
  );

  const selectedLocation = useMemo(() => {
    const current = draftToShipToAddress(draft);
    if (!current) {
      return undefined;
    }
    const match = locationItems.find((item) =>
      shipToAddressesMatch(current, item.value),
    );
    return (
      match ??
      makeShipToLocationItem(
        "current-ship-to-address",
        current,
        "Current destination",
      )
    );
  }, [draft, locationItems]);

  const applyAddress = useCallback(
    (address: SelectableShipToAddress) => {
      setDraft({
        ...draft,
        shipToName: address.name,
        shipToStreet1: address.street1,
        shipToStreet2: address.street2 ?? "",
        shipToCity: address.city,
        shipToState: address.state ?? "",
        shipToZip: address.zip,
        shipToCountry: address.country,
      });
    },
    [draft, setDraft],
  );

  const selectedHubOption = useMemo(
    () =>
      EXTERNAL_HUB_OPTIONS.find((o) => o.value === hubDraft?.provider) ??
      EXTERNAL_HUB_OPTIONS[0],
    [hubDraft?.provider],
  );

  const locationsQuery = useRpcQuery(
    RedoAdminRpcClientContext,
    "getTeamLocationsForAdmin",
    { teamId },
    { enabled: !hasSourceLocation },
  );

  const setLinkedMutation = useRpcMutation(RedoAdminRpcClientContext, {
    method: "setLocationLinkedReturnsCenter",
  });

  const locations = useMemo(
    () => locationsQuery.data?.locations ?? [],
    [locationsQuery.data],
  );

  const [linkedLocationDrafts, setLinkedLocationDrafts] =
    useState<LinkedLocationDrafts>({});

  const draftReturnsCenterId = useMemo(
    () => returnsCenterIdFormat.schema.parse(draft.returnsCenterId),
    [draft.returnsCenterId],
  );

  const merchantSourceLocation = useMemo(
    () =>
      locations.find(
        (location) =>
          getDraftLinkedReturnsCenterId(location, linkedLocationDrafts) ===
          draftReturnsCenterId,
      ) ?? null,
    [draftReturnsCenterId, linkedLocationDrafts, locations],
  );

  const merchantSourceLocationOptions = useMemo(() => {
    const linkableItems = locations
      .filter(
        (location) =>
          location.returnsCenterId === null &&
          getDraftLinkedReturnsCenterId(location, linkedLocationDrafts) ===
            null,
      )
      .map(merchantLocationToListItem);

    return merchantSourceLocation
      ? [
          merchantLocationToListItem(merchantSourceLocation),
          NO_MERCHANT_SOURCE_LOCATION_ITEM,
          ...linkableItems,
        ]
      : linkableItems;
  }, [linkedLocationDrafts, locations, merchantSourceLocation]);

  const hasLinkedLocationChanges = useMemo(
    () =>
      locations.some(
        (location) =>
          hasLinkedLocationDraft(linkedLocationDrafts, location.id) &&
          linkedLocationDrafts[location.id] !== location.linkedReturnsCenterId,
      ),
    [linkedLocationDrafts, locations],
  );

  const setMerchantSourceLocationDraft = useCallback(
    (location: TeamLocation | null) => {
      setLinkedLocationDrafts((current) => {
        const next = { ...current };

        const currentLocation = locations.find(
          (existingLocation) =>
            getDraftLinkedReturnsCenterId(existingLocation, current) ===
            draftReturnsCenterId,
        );
        if (currentLocation) {
          setDraftLinkedReturnsCenterId(next, currentLocation, null);
        }
        if (location) {
          setDraftLinkedReturnsCenterId(next, location, draftReturnsCenterId);
        }

        return next;
      });
    },
    [draftReturnsCenterId, locations],
  );

  const saveLinkedLocationDrafts = useCallback(async () => {
    const changes = locations.flatMap((location) => {
      if (!hasLinkedLocationDraft(linkedLocationDrafts, location.id)) {
        return [];
      }
      const linkedReturnsCenterId = linkedLocationDrafts[location.id] ?? null;
      if (linkedReturnsCenterId === location.linkedReturnsCenterId) {
        return [];
      }
      return [{ locationId: location.id, linkedReturnsCenterId }];
    });

    for (const change of changes) {
      await setLinkedMutation.mutateAsync({
        teamId,
        locationId: change.locationId,
        linkedReturnsCenterId: change.linkedReturnsCenterId,
      });
    }

    if (changes.length > 0) {
      await locationsQuery.refetch();
      toast("Merchant locations updated", { variant: "success" });
    }
  }, [
    linkedLocationDrafts,
    locations,
    locationsQuery,
    setLinkedMutation,
    teamId,
  ]);

  const handleSave = useCallback(async () => {
    try {
      await saveLinkedLocationDrafts();
    } catch (error) {
      toast("Failed to update merchant locations", { variant: "error" });
      throw error;
    }
    onSave();
  }, [onSave, saveLinkedLocationDrafts]);

  // Hub-only mode doesn't render the billing/consolidation fields, so their
  // pre-existing values must not block that save.
  const scheduleDaysError =
    editMode === "full"
      ? returnsCenterScheduleDaysErrors(draft.consolidationScheduleDays)[0]
      : undefined;
  const processingFeeError =
    editMode === "full"
      ? processingFeeErrors(draft.processingFee)[0]
      : undefined;
  const feeBasisError =
    editMode === "full" ? returnsCenterFeeBasisErrors(draft)[0] : undefined;
  const billingUnavailable = globalFeeBasis == null;

  return (
    <RedoModal
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Save",
        onClick: handleSave,
        disabled:
          setLinkedMutation.isPending ||
          !!scheduleDaysError ||
          !!processingFeeError ||
          !!feeBasisError,
        size: "xs",
      }}
      secondaryButton={{
        text: "Cancel",
        onClick: onClose,
        disabled: false,
        size: "xs",
      }}
      subtitle={
        editMode === "hub-only"
          ? "Configure the external hub provider for this returns center."
          : "Configure billing, consolidation, and external hub for this returns center."
      }
      title={centerLabel}
    >
      <Flex dir="column" gap="lg">
        {editMode === "full" && (
          <>
            <Flex dir="column" gap="sm">
              <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
                Billing
              </Text>
              <RedoTextInput
                errorText={processingFeeError}
                IconTrailing={() => <Text as="span">USD</Text>}
                inputTypeHint="number"
                label="Processing fee override"
                min={0}
                placeholder="0.00"
                setValue={updateProcessingFee}
                state={billingUnavailable ? "disabled" : "default"}
                step={0.01}
                value={draft.processingFee}
              />
              <Text fontSize="xs" textColor="secondary">
                If empty, uses the default processing fee for this merchant. A
                fee entered here is pinned and does not follow later changes to
                the merchant default.
              </Text>
              <Flex dir="column" gap="xs">
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  Fee basis
                </Text>
                <RedoButtonTabGroup
                  disabled={
                    billingUnavailable || draft.processingFee.trim() === ""
                  }
                  fullWidth={false}
                  onChange={(value) => updateField("processingFeeBasis", value)}
                  options={FEE_BASIS_OPTIONS}
                  size="sm"
                  value={
                    (draft.processingFee.trim() === ""
                      ? globalFeeBasis
                      : (draft.processingFeeBasis ?? globalFeeBasis)) ??
                    ProcessingFeeBasis.PerReturn
                  }
                />
              </Flex>
              {feeBasisError ? (
                <Text fontSize="xs" textColor="error">
                  {feeBasisError}
                </Text>
              ) : (
                <Text fontSize="xs" textColor="secondary">
                  Without a fee override, this center bills on the
                  merchant&apos;s fee basis.
                </Text>
              )}
              {billingUnavailable && (
                <Text fontSize="xs" textColor="error">
                  Billing settings failed to load; reload the page to edit
                  pricing.
                </Text>
              )}
            </Flex>
            <Flex dir="column" gap="sm">
              <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
                Returns Consolidation
              </Text>
              <RedoTextInput
                errorText={scheduleDaysError}
                inputTypeHint="number"
                label="Days between shipments"
                min={1}
                placeholder="e.g. 7"
                setValue={(v) => updateField("consolidationScheduleDays", v)}
                step={1}
                value={draft.consolidationScheduleDays}
              />
              <Text fontSize="xs" textColor="secondary">
                Controls how often consolidated returns are shipped for this
                center.
              </Text>
            </Flex>
            <Flex dir="column" gap="sm">
              <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
                Consolidation ship-to address
              </Text>
              <RedoSingleSelectDropdownInput<SelectableShipToAddress>
                description="The saved location that receives consolidated returns shipped from this center."
                emptyListMessage="No saved locations for this team"
                fullWidth
                itemsLoading={addressesQuery.isLoading}
                options={locationItems}
                optionSelected={({ item }) => applyAddress(item.value)}
                placeholder="Select a saved location"
                selectedItem={selectedLocation}
                size="xs"
              />
            </Flex>
          </>
        )}
        {setHubDraft && hubDraft && (
          <Flex dir="column" gap="sm">
            <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
              External Hub
            </Text>
            <RedoSingleSelectDropdownInput<ExternalReturnHubType | "">
              label="Provider"
              options={EXTERNAL_HUB_OPTIONS}
              optionSelected={({ item }) =>
                setHubDraft({
                  provider: item.value,
                  locationId: item.value ? hubDraft.locationId : "",
                })
              }
              placeholder="Select provider"
              selectedItem={selectedHubOption}
              size="sm"
            />
            {hubDraft.provider && (
              <RedoTextInput
                label="Location ID"
                placeholder="Enter location ID"
                setValue={(v) => setHubDraft({ ...hubDraft, locationId: v })}
                value={hubDraft.locationId}
              />
            )}
            <Text fontSize="xs" textColor="secondary">
              Route returns at this center through an external provider.
            </Text>
          </Flex>
        )}
        {!hasSourceLocation && (
          <MerchantSourceLocationSection
            disabled={disabled}
            hasUnsavedChanges={hasLinkedLocationChanges}
            isError={locationsQuery.isError}
            isLoading={locationsQuery.isLoading}
            onSelectLocation={setMerchantSourceLocationDraft}
            options={merchantSourceLocationOptions}
            pending={setLinkedMutation.isPending}
            selectedLocation={merchantSourceLocation}
          />
        )}
      </Flex>
    </RedoModal>
  );
});

function formatProvider(provider: string | null): string {
  if (!provider) {
    return "";
  }
  return provider.charAt(0).toUpperCase() + provider.slice(1);
}

function formatMerchantLocationDetail(location: TeamLocation): string {
  const addressSummary = [location.city, location.state, location.country]
    .filter(Boolean)
    .join(", ");
  return [
    formatProvider(location.provider),
    location.externalId ? `ID ${location.externalId}` : "",
    addressSummary,
    location.isActive === false ? "Inactive" : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

function merchantLocationToListItem(
  location: TeamLocation,
): RedoListItem<TeamLocation | null> {
  return {
    id: location.id,
    supportingText: formatMerchantLocationDetail(location),
    text: location.name,
    type: "text",
    value: location,
  };
}

const NO_MERCHANT_SOURCE_LOCATION_ITEM: RedoListItem<TeamLocation | null> = {
  id: "no-merchant-source-location",
  text: "No merchant location",
  type: "text",
  value: null,
};

type LinkedLocationDrafts = Record<string, ReturnsCenterId | null>;

function hasLinkedLocationDraft(
  drafts: LinkedLocationDrafts,
  locationId: string,
): boolean {
  return Object.prototype.hasOwnProperty.call(drafts, locationId);
}

function getDraftLinkedReturnsCenterId(
  location: TeamLocation,
  drafts: LinkedLocationDrafts,
): ReturnsCenterId | null {
  return hasLinkedLocationDraft(drafts, location.id)
    ? (drafts[location.id] ?? null)
    : location.linkedReturnsCenterId;
}

function setDraftLinkedReturnsCenterId(
  drafts: LinkedLocationDrafts,
  location: TeamLocation,
  linkedReturnsCenterId: ReturnsCenterId | null,
): void {
  if (location.linkedReturnsCenterId === linkedReturnsCenterId) {
    delete drafts[location.id];
  } else {
    drafts[location.id] = linkedReturnsCenterId;
  }
}

const MerchantSourceLocationSection = memo(
  function MerchantSourceLocationSection({
    disabled,
    hasUnsavedChanges,
    isError,
    isLoading,
    onSelectLocation,
    options,
    pending,
    selectedLocation,
  }: {
    disabled: boolean;
    hasUnsavedChanges: boolean;
    isError: boolean;
    isLoading: boolean;
    onSelectLocation: (location: TeamLocation | null) => void;
    options: RedoListItem<TeamLocation | null>[];
    pending: boolean;
    selectedLocation: TeamLocation | null;
  }) {
    const controlsDisabled = disabled || pending;
    const selectedItem = selectedLocation
      ? merchantLocationToListItem(selectedLocation)
      : undefined;

    const selectLocation = useCallback(
      ({ item }: { item: RedoListItem<TeamLocation | null> }) => {
        onSelectLocation(item.value);
      },
      [onSelectLocation],
    );

    return (
      <Flex dir="column" gap="sm">
        <Flex dir="column" gap="xxs">
          <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
            Merchant source location
          </Text>
          <Text fontSize="xs" textColor="secondary">
            Select one merchant location to use for inventory and fulfillment
            data.
          </Text>
        </Flex>
        <RedoSingleSelectDropdownInput<TeamLocation | null>
          disabled={controlsDisabled}
          emptyListMessage="No merchant locations available"
          fullWidth
          itemsLoading={isLoading}
          options={options}
          optionSelected={selectLocation}
          placeholder="Select merchant location..."
          selectedItem={selectedItem}
          size="xs"
        />
        {selectedLocation && (
          <Text fontSize="xs" textColor="secondary">
            {formatMerchantLocationDetail(selectedLocation)}
          </Text>
        )}
        {isError && (
          <Text fontSize="xs" textColor="error">
            Failed to load merchant locations.
          </Text>
        )}
        {hasUnsavedChanges && (
          <Text fontSize="xs" textColor="secondary">
            Changes save when you click Save.
          </Text>
        )}
      </Flex>
    );
  },
);
